import React, { useEffect, useMemo, useState } from 'react';
import { Button, Steps, Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';
import { useWatch } from 'antd/es/form/Form';
import Information from './Information';
import FirstStep from './Steps/FirstStep';
import SecondStep from './Steps/SecondStep';
import ThirdStep from './Steps/ThirdStep';
import LastStep from './Steps/LastStep';
import { useUserUpdate } from '../../../hooks/api/createaccount';
import { actions, useAuth } from '../../../store/auth';

import styles from './index.module.scss';
import usePreventNavigation from '../../../hooks/usePreventNavigation';
import { checkIsProfileIncomplete } from '../../../routes/AuthRedirect';

export default function CreateAccount(): React.ReactElement {
  // Prevent redirect if user has incomplete profile >>>
  const { user } = useAuth();
  const profileIncomplete = useMemo(() => checkIsProfileIncomplete(user), [user]);

  const { modal } = usePreventNavigation(
    profileIncomplete,
    {
      title: 'Please finish creating your account.',
      description: 'You need to complete your profile before leaving this page.',
    },
  );
  // Prevent redirect <<<

  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm();
  const [frequency, setFrequency] = useState(false);
  const [formData, setFormData] = useState<{
    countryId?: number;
    tipFrequency?: string;
    [key: string]: any;
  }>({});
  const [countryId, setCountryId] = useState<number | null>(null);
  const userUpdate = useUserUpdate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (userUpdate.data) {
      userUpdate.clearResponse();
      navigate('/create-account-success');
    }
  }, [userUpdate]);

  const next = async () => {
    try {
      const values = await form.validateFields();

      const newFormData = { ...formData, ...values };

      setFormData(newFormData);

      if (values.countryId) {
        setCountryId(values.countryId);
      }

      if (frequency) {
        handleFinish();
      } else {
        setCurrent(current + 1);
      }
    } catch (error) {
      console.log('Validation Failed:', error);
    }
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  const handleFinish = async () => {
    try {
      const values = await form.validateFields();
      const finalData = { ...formData, ...values, isCreating: true };

      delete finalData.occupation;

      if (finalData.categoryId === 'custom') {
        delete finalData.categoryId;
      }

      setIsLoading(true);
      const result = await userUpdate.fetch(finalData);

      if (result?.success) {
        dispatch(actions.updateUserRequest(finalData));
        setIsComplete(true);
      }

      setIsLoading(false);
    } catch (error) {
      console.log('Validation Failed:', error);
      setIsLoading(false);
    }
  };

  const steps = React.useMemo(() => {
    if (countryId === 233) {
      if (frequency) {
        return [
          { title: '1', component: FirstStep },
          { title: '2', component: SecondStep },
        ];
      }

      return [
        { title: '1', component: FirstStep },
        { title: '2', component: SecondStep },
        { title: '3', component: ThirdStep },
        { title: '4', component: LastStep },
      ];
    }

    return [
      { title: '1', component: FirstStep },
      { title: '2', component: LastStep },
    ];
  }, [countryId, frequency]);

  const CurrentStepComponent = steps[current].component;

  useEffect(() => {
    if (isComplete) {
      navigate('/create-account-success');
    }
  }, [isComplete]);

  const occupation = useWatch<string>('categoryId', form);

  return (
    <div className={styles.accountContent}>
      {user !== null && !isComplete ? modal : null}
      <div className={styles.content}>
        <div className={styles.contentHeader}>
          <h4>Create Account</h4>
          <Steps
            current={current}
            responsive={false}
            items={steps.map(({ title }) => ({ key: title, title: '' }))}
            className={clsx(styles.steps, {
              [styles.smallSteps]: countryId !== 233 || frequency,
            })}
          />
        </div>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ tipType: 'percent' }}
        >
          <CurrentStepComponent
            form={form}
            setCountryId={setCountryId}
            countryId={countryId || undefined}
            setFrequency={setFrequency}
          />
        </Form>
        <div style={{ marginTop: 24 }} className={styles.btn}>
          {current > 0 && (
            <Button size="large" style={{ margin: '0 8px' }} onClick={prev}>
              Previous
            </Button>
          )}
          {current < steps.length - 1 && (
            <Button
              style={{ marginLeft: 'auto' }}
              size="large"
              disabled={current === 2 && !occupation}
              type="primary"
              onClick={next}
            >
              Next
            </Button>
          )}
          {current === steps.length - 1 && (
            <Button
              loading={isLoading}
              size="large"
              type="primary"
              onClick={handleFinish}
            >
              Next
            </Button>
          )}
        </div>
      </div>
      {current === 2 && countryId === 233 ? null : (
        <div className={styles.information}>
          <Information current={current} countryId={countryId || undefined} />
        </div>
      )}
    </div>
  );
}
